import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { App } from 'antd';
import { toqueAtual } from '@titularis/analytics';
import type { Product, ProductPrice } from '@titularis/api-client';
import { api } from './api';
import { registrarEvento, type OrigemDaCompra } from './analytics';

/** Centavos do servidor, reais na tela. */
export const reais = (centavos: number) =>
  (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Do mais barato ao mais caro: o Pix vem primeiro, e é onde queremos o olho. */
export const porPreco = (a: ProductPrice, b: ProductPrice) => a.amountCents - b.amountCents;

/*
 * O `planosDe` mora em `@titularis/ui`, com o `rotuloDoPlano`.
 *
 * Ele nasceu aqui e a landing pagou por isso: `apps/site` tinha a propria
 * leitura do catalogo, chaveada por forma de pagamento, e no dia em que o
 * produto ganhou cinco precos ela mostrou cinco chips e anunciou o mensal como
 * "a vista no Pix". Reexportado para os importadores daqui continuarem valendo.
 */
export { planosDe, type PlanoDoProduto } from '@titularis/ui';

/** O que a forma de pagamento promete, em uma linha. */
export function descreverPreco(preco: ProductPrice): string {
  const cobranca =
    preco.recurring
      ? preco.accessMonths === 1
        ? `${reais(preco.amountCents)} por mês`
        : `${reais(preco.amountCents)} a cada ${preco.accessMonths} meses`
      : preco.maxInstallments > 1
        ? `${preco.maxInstallments}x de ${reais(Math.round(preco.amountCents / preco.maxInstallments))}`
        : `${reais(preco.amountCents)} à vista`;

  if (preco.accessMonths === 1) return cobranca;

  const equivalenteMensal = reais(Math.round(preco.amountCents / preco.accessMonths));
  return `${cobranca} · equivalente a ${equivalenteMensal}/mês`;
}

/**
 * O produto que libera uma chave, escolhido pelo catálogo do servidor.
 *
 * A regra é a mesma que a landing já usa: **o primeiro produto cujo `grants`
 * contém a chave bloqueada**. Sem chave - quem abriu a oferta por vontade
 * própria -, cai no primeiro produto à venda, que é melhor que não oferecer nada.
 *
 * **Com chave e sem ninguém que a conceda, não devolve nada** (D-44). Antes caía
 * no primeiro produto do catálogo, e isso deixou de ser inofensivo quando cada
 * Legislação Comentada virou um produto: quem esbarra no cadeado do Provimento
 * 149 receberia a oferta do Enac, compraria, e continuaria sem o Provimento 149.
 * Vender o que não abre o que a pessoa clicou é pior que dizer que ainda não
 * está à venda.
 */
export function ofertaPara(catalogo: Product[], chave?: string | null): Product | undefined {
  if (chave) return catalogo.find((produto) => produto.grants.includes(chave));
  return catalogo[0];
}

/**
 * O que ainda dá para vender a este aluno.
 *
 * **A pergunta se responde contra o catálogo do servidor, nunca contra um mapa
 * escrito no bundle** (G-POLICY). A tela de Plano perguntava ao `PRODUTOS` de
 * `produtos.ts`, que lista `enac`, `objective` e `essay`: quem comprava o Enac
 * "ficava faltando" a 1ª e a 2ª fase, que não estão à venda, não têm produto no
 * catálogo e aparecem com cadeado no próprio menu. O rodapé então se desenhava,
 * e o rótulo do botão vinha de um terceiro lugar - `catalogo[0].name` -, de modo
 * que a tela de "Pagamento confirmado" oferecia o Enac a quem acabara de
 * comprá-lo. Quem clicava recebia do servidor "Você já tem esse acesso até ...".
 *
 * A regra é a **mesma do `canBuy` do servidor** (G-ISONOMIA): um produto está
 * à venda mesmo quando o acesso está vigente; só sai o preço que já tem uma
 * assinatura viva. A recusa por direito caiu quando a concessão passou a somar
 * prazo, e mantê-la só no app esconderia upgrades que o servidor aceita.
 */
export function produtosAVenda(
  catalogo: Product[],
  assinaturasVivas: readonly { priceId: string }[],
): Product[] {
  const assinados = new Set(assinaturasVivas.map((assinatura) => assinatura.priceId));
  return catalogo
    .map((produto) => ({
      ...produto,
      precos: produto.precos.filter((preco) => !assinados.has(preco.id)),
    }))
    .filter((produto) => produto.precos.length > 0);
}

/**
 * O caminho até o pagamento, num lugar só.
 *
 * Ele vivia inteiro dentro da `PlanosPage`. Saiu daqui em 05/09/2026 porque o
 * paywall e as boas-vindas passaram a vender também, e **esta é a única função
 * do app que manda alguém para fora com dinheiro na mão**: duplicá-la em três
 * telas significaria três lugares para esquecer de registrar o evento, três
 * tratamentos de erro divergindo e três chances de o `location.href` virar um
 * `navigate` por engano.
 *
 * A `origem` não é enfeite de telemetria: é a pergunta comercial desta rodada -
 * o modal do bloqueio vende mais que a vitrine? -, e ela só tem resposta se
 * cada superfície se identificar na hora de começar.
 */
export interface Compra {
  /** Dispara o checkout. Em caso de sucesso, a aba sai do app. */
  comprar: (escolha: EscolhaDeCompra) => void;
  /** `produto:priceId` do botão que está esperando resposta, para o `loading`. */
  emAndamento: string | null;
  /** Enquanto uma compra está a caminho, os outros botões ficam parados. */
  ocupado: boolean;
}

interface EscolhaDeCompra {
  produto: string;
  priceId: string;
  forma: 'card' | 'pix';
  meses: number;
  recorrente: boolean;
}

export function useComprar(origem: OrigemDaCompra): Compra {
  const { message } = App.useApp();
  const [emAndamento, setEmAndamento] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (escolha: EscolhaDeCompra) =>
      /*
       * O canal vai junto com a compra e é congelado no pedido.
       *
       * `toqueAtual()` e não `primeiroToque()`: aqui a pergunta é "o que estava
       * na tela quando a pessoa decidiu pagar". Quem trouxe a pessoa ao produto
       * é outra pergunta, e a resposta dela já está gravada na conta desde o
       * cadastro. Guardar o primeiro toque nos dois lugares faria duas colunas
       * responderem a mesma coisa e nenhuma responder à outra.
       */
      api.createCheckout({
        produto: escolha.produto,
        priceId: escolha.priceId,
        atribuicao: toqueAtual(),
      }),
    onMutate: (escolha) => {
      setEmAndamento(`${escolha.produto}:${escolha.priceId}`);
      registrarEvento({
        nome: 'checkout_started',
        produto: escolha.produto,
        forma: escolha.forma,
        meses: escolha.meses,
        recorrente: escolha.recorrente,
        origem,
      });
    },
    onSuccess: ({ url }) => {
      /*
       * `location.href`, e não `navigate`: a tela de pagamento é do provedor e
       * mora fora deste app. Sair daqui é o comportamento certo, e voltar é
       * responsabilidade das URLs de retorno que o checkout carrega.
       */
      window.location.href = url;
    },
    onError: (erro: Error) => {
      setEmAndamento(null);
      message.error(erro.message);
    },
  });

  return {
    comprar: (escolha) => mutation.mutate(escolha),
    emAndamento,
    ocupado: mutation.isPending,
  };
}
